import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Zap, ChevronDown, Search, X, Check, AlertCircle, GripVertical } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { isValidModelId, getInvalidModelVoices, getModelValidationMessage } from '../lib/modelValidation';
import { DebateConfig } from '../components/DebateConfig';
import { ContextAttacher } from '../components/ContextAttacher';
import { GitHubContextAttacher } from '../components/GitHubContextAttacher';
import AlteredStatePicker from '../components/AlteredStatePicker';
import UpgradePrompt from '../components/UpgradePrompt';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };
const TIER_ORDER = { economy: 0, standard: 1, premium: 2 };
const VOICE_COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#A855F7', '#F59E0B', '#06B6D4', '#EC4899'];

const DEFAULT_VOICE = {
  name: '',
  mandate: '',
  model: 'google/gemini-2.5-flash',
  model_tier: 'economy',
  color: '#3B82F6',
};

export default function CustomFray() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [voices, setVoices] = useState([
    { ...DEFAULT_VOICE, name: 'Voice 1', mandate: '', color: VOICE_COLORS[0] },
    { ...DEFAULT_VOICE, name: 'Voice 2', mandate: '', color: VOICE_COLORS[1] },
  ]);
  const [scenario, setScenario] = useState('');
  const [numPhases, setNumPhases] = useState(3);
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [debateMode, setDebateMode] = useState('panel');
  const [frictionLevel, setFrictionLevel] = useState(3);
  const [catFightTheme, setCatFightTheme] = useState('boxing_ring');
  const [contextIds, setContextIds] = useState([]);
  const [topicLoading, setTopicLoading] = useState(false);
  const [alteredState, setAlteredState] = useState('');
  const [githubContext, setGithubContext] = useState(null);

  useEffect(() => {
    if (user) loadModels();
  }, [user]);

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const res = await API.get('/api/byok/models');
      const sorted = (res.data.models || []).sort((a, b) => {
        const td = (TIER_ORDER[a.tier] || 0) - (TIER_ORDER[b.tier] || 0);
        return td !== 0 ? td : a.name.localeCompare(b.name);
      });
      setAvailableModels(sorted);
    } catch {}
    setModelsLoading(false);
  };

  const addVoice = () => {
    if (voices.length >= 7) return;
    setVoices(prev => [...prev, {
      ...DEFAULT_VOICE,
      name: `Voice ${prev.length + 1}`,
      color: VOICE_COLORS[prev.length % VOICE_COLORS.length],
    }]);
  };

  const removeVoice = (idx) => {
    if (voices.length <= 2) return;
    setVoices(prev => prev.filter((_, i) => i !== idx));
  };

  const updateVoice = (idx, field, value) => {
    setVoices(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  const updateVoiceModel = (idx, modelId, modelName, tier) => {
    setVoices(prev => prev.map((v, i) =>
      i === idx ? { ...v, model: modelId, model_tier: tier } : v
    ));
  };

  const handleLaunch = async () => {
    if (!user) { navigate('/login'); return; }
    if (!scenario.trim()) { setError('Enter your prompt / scenario'); return; }

    const emptyVoice = voices.find(v => !v.name.trim());
    if (emptyVoice) { setError('All voices need a name'); return; }

    // Validate model IDs
    const invalid = getInvalidModelVoices(voices);
    if (invalid.length > 0) {
      setError(getModelValidationMessage(invalid));
      return;
    }

    setLaunching(true);
    setError('');
    try {
      const res = await API.post('/api/frays', {
        scenario,
        num_phases: debateMode === 'cat_fight' ? 1 : numPhases,
        debate_mode: debateMode,
        friction_level: debateMode === 'cat_fight' ? 10 : frictionLevel,
        cat_fight_theme: debateMode === 'cat_fight' ? catFightTheme : undefined,
        altered_state: alteredState || undefined,
        context_ids: contextIds,
        github_context: githubContext ? { repo: githubContext.repo, paths: githubContext.paths } : undefined,
        custom_voices: voices.map(v => ({
          name: v.name,
          mandate: v.mandate || `Provide expert analysis as ${v.name}.`,
          model: v.model,
          model_tier: v.model_tier,
          color: v.color,
        })),
      });
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create Fray');
      setLaunching(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-mono text-[#FF4D00]">CUSTOM</span>
              <span className="text-[10px] px-2 py-0.5 border border-[#FF4D00]/30 text-[#FF4D00] font-mono">USER DEFINED</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed']" data-testid="custom-fray-title">User Defined Fray</h1>
            <p className="text-[#A0A0A0] text-base mt-2">
              Build your own committee from scratch. Define each Voice, assign models, and paste your prompt.
            </p>
          </div>

          {/* Scenario / Prompt */}
          <div className="mb-10" data-testid="prompt-section">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-2">Your Prompt</h2>
            <p className="text-sm text-[#A0A0A0] mb-4">Paste the scenario, question, or document you want the committee to evaluate.</p>
            <textarea
              value={scenario}
              onChange={e => setScenario(e.target.value)}
              className="w-full h-44 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm resize-none focus:outline-none focus:border-[#FF4D00] transition-colors leading-relaxed"
              placeholder="Paste your prompt here — a business plan, technical spec, thesis draft, policy proposal, investment thesis, or any decision that needs multi-perspective analysis..."
              data-testid="custom-scenario-input"
            />
            <div className="flex items-center gap-3 mt-2">
              <span className="text-[10px] font-mono text-[#A0A0A0]">{scenario.length} characters</span>
              {debateMode === 'cat_fight' && (
                <>
                  <button onClick={async () => {
                    setTopicLoading(true);
                    try {
                      const res = await API.get('/api/cat-fight/random-topic');
                      setScenario(res.data.topic);
                      if (res.data.suggested_voices && res.data.suggested_voices.length > 0) {
                        setVoices(res.data.suggested_voices.map((sv, i) => ({
                          name: sv.name, mandate: sv.position,
                          model: 'google/gemini-2.5-flash', model_tier: 'economy',
                          color: VOICE_COLORS[i % VOICE_COLORS.length],
                        })));
                      }
                    } catch {}
                    setTopicLoading(false);
                  }} disabled={topicLoading}
                    className="px-3 py-1 min-h-[44px] md:min-h-0 bg-[#EF4444] text-white text-[10px] font-mono disabled:opacity-50 hover:bg-[#EF4444]/80 transition-colors">
                    {topicLoading ? '...' : 'Random Topic'}
                  </button>
                  <button onClick={async () => {
                    setTopicLoading(true);
                    try {
                      const res = await API.post('/api/cat-fight/generate-topic', {});
                      setScenario(res.data.topic);
                      if (res.data.suggested_voices && res.data.suggested_voices.length > 0) {
                        setVoices(res.data.suggested_voices.map((sv, i) => ({
                          name: sv.name, mandate: sv.position,
                          model: 'google/gemini-2.5-flash', model_tier: 'economy',
                          color: VOICE_COLORS[i % VOICE_COLORS.length],
                        })));
                      }
                    } catch {}
                    setTopicLoading(false);
                  }} disabled={topicLoading}
                    className="px-3 py-1 min-h-[44px] md:min-h-0 border border-[#EF4444]/30 text-[#EF4444] text-[10px] font-mono disabled:opacity-50 hover:bg-[#EF4444]/10 transition-colors">
                    {topicLoading ? '...' : 'Surprise Me'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Voices */}
          <div className="mb-10" data-testid="voices-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold font-['Barlow_Condensed']">Committee Voices</h2>
              <span className="text-xs font-mono text-[#A0A0A0]">{voices.length} / 7 voices</span>
            </div>
            <p className="text-sm text-[#A0A0A0] mb-4">
              Define 2-7 Voices. Give each a name, a mandate (their perspective/role), and select an AI model.
            </p>

            {availableModels.length > 0 && (
              <p className="text-[10px] font-mono text-[#22C55E] mb-4" data-testid="models-count">
                {availableModels.length} models available from your OpenRouter key
              </p>
            )}

            <div className="space-y-4 mb-4">
              {voices.map((voice, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]"
                  data-testid={`custom-voice-${i}`}
                >
                  <div className="flex items-start gap-2 md:gap-3">
                    {/* Color indicator */}
                    <div className="w-8 h-8 flex items-center justify-center border-2 shrink-0 text-xs font-bold font-['Barlow_Condensed'] mt-1"
                      style={{ borderColor: voice.color, color: voice.color }}>
                      {(voice.name || '?').charAt(0).toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0 space-y-3">
                      {/* Voice name */}
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={voice.name}
                          onChange={e => updateVoice(i, 'name', e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-['Barlow_Condensed'] font-medium text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                          placeholder="Voice name (e.g., Devil's Advocate)"
                          data-testid={`voice-name-${i}`}
                        />
                        {voices.length > 2 && (
                          <button
                            onClick={() => removeVoice(i)}
                            className="p-2 md:p-1.5 text-[#A0A0A0] hover:text-[#EF4444] transition-colors min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center"
                            data-testid={`remove-voice-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Mandate */}
                      <textarea
                        value={voice.mandate}
                        onChange={e => updateVoice(i, 'mandate', e.target.value)}
                        className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs text-[#F5F5F5] resize-none focus:outline-none focus:border-[#FF4D00] h-16 leading-relaxed"
                        placeholder="Mandate — what perspective should this Voice bring? (e.g., Challenge every assumption. Find fatal flaws.)"
                        data-testid={`voice-mandate-${i}`}
                      />

                      {/* Model picker */}
                      <ModelPicker
                        voice={voice}
                        index={i}
                        availableModels={availableModels}
                        onSelectModel={(modelId, modelName, tier) => updateVoiceModel(i, modelId, modelName, tier)}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {voices.length < 7 ? (
              <button
                onClick={addVoice}
                className="w-full py-3 min-h-[44px] border border-dashed border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#FF4D00] hover:border-[#FF4D00]/40 transition-colors flex items-center justify-center gap-2"
                data-testid="add-voice-btn"
              >
                <Plus className="w-4 h-4" /> Add Voice ({voices.length}/7)
              </button>
            ) : (
              <div className="mt-2">
                <UpgradePrompt
                  feature="voices"
                  currentLimit={7}
                  planNeeded="team"
                  message="You've reached the max voices. Upgrade for larger committees."
                />
              </div>
            )}
          </div>

          {/* Phases */}
          <div className="mb-10">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-2">Debate Rounds</h2>
            <p className="text-sm text-[#A0A0A0] mb-4">How many phases of deliberation?</p>
            <div className="flex flex-wrap gap-2">
              {[2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setNumPhases(n)}
                  className={`px-4 py-2 min-h-[44px] text-sm font-mono border transition-colors ${
                    numPhases === n ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]/40'
                  }`}
                  data-testid={`phases-${n}`}
                >
                  {n} Phases
                </button>
              ))}
            </div>
          </div>

          {/* Debate Mode + Friction */}
          <DebateConfig debateMode={debateMode} setDebateMode={setDebateMode} frictionLevel={frictionLevel} setFrictionLevel={setFrictionLevel} catFightTheme={catFightTheme} setCatFightTheme={setCatFightTheme} />

          {/* Altered State */}
          <div className="mt-6 mb-10">
            <label className="text-xs text-[#A0A0A0] font-mono mb-2 block">ALTERED STATE (Optional)</label>
            <AlteredStatePicker selected={alteredState} onSelect={setAlteredState} />
          </div>

          {/* Context */}
          <ContextAttacher selectedIds={contextIds} onChange={setContextIds} />

          {/* GitHub Context */}
          <GitHubContextAttacher
            githubContext={githubContext}
            setGithubContext={setGithubContext}
          />

          {/* Launch */}
          {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="custom-error">{error}</div>}

          <div className="p-4 md:p-6 bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-1">Launch</h3>
                <p className="text-sm text-[#A0A0A0]">
                  {voices.length} Voices, {numPhases} phases
                  {user?.billing_mode === 'byok' ? ' — using your OpenRouter key' : ''}
                </p>
              </div>
              <button
                onClick={handleLaunch}
                disabled={launching || !scenario.trim() || voices.some(v => !v.name.trim())}
                className="w-full md:w-auto px-8 py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold flex items-center justify-center gap-2 hover:bg-[#FF4D00]/90 transition-colors disabled:opacity-40"
                data-testid="launch-custom-fray-btn"
              >
                {launching ? (
                  <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Sharpening claws...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Enter the Fray</>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Model Picker (reusable)                                             */
/* ------------------------------------------------------------------ */
function ModelPicker({ voice, index, availableModels, onSelectModel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = availableModels.filter(m => {
    const matchSearch = !search || m.id.toLowerCase().includes(search.toLowerCase()) || m.name.toLowerCase().includes(search.toLowerCase());
    const matchTier = tierFilter === 'all' || m.tier === tierFilter;
    return matchSearch && matchTier;
  });

  const hasModels = availableModels.length > 0;
  const currentModel = voice.model || voice.default_model;
  const currentTier = voice.model_tier;
  const modelInvalid = !isValidModelId(currentModel);

  return (
    <div className="relative" ref={dropdownRef}>
      {modelInvalid && (
        <div className="text-[9px] text-[#EF4444] font-mono mb-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Invalid model ID — select a valid model below
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-2.5 min-h-[44px] border text-left transition-all group ${
          modelInvalid ? 'border-[#EF4444] bg-[#EF4444]/5' :
          open ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/50 hover:bg-[#2A2A2A]/30'
        }`}
        data-testid={`model-picker-${index}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] px-1.5 py-0.5 border font-mono shrink-0"
            style={{ color: TIER_COLORS[currentTier], borderColor: TIER_COLORS[currentTier] + '40' }}>
            {currentTier?.toUpperCase()}
          </span>
          <span className="text-xs font-mono text-[#F5F5F5] truncate">{currentModel}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <span className="text-[9px] text-[#FF4D00] font-mono opacity-0 group-hover:opacity-100 transition-opacity">
            {hasModels ? 'change' : 'setup key'}
          </span>
          <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${open ? 'rotate-180 text-[#FF4D00]' : ''}`} />
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl shadow-black/50 max-h-[60vh] md:max-h-[280px] flex flex-col"
            data-testid={`model-dropdown-${index}`}
          >
            {!hasModels ? (
              <div className="p-6 text-center">
                <AlertCircle className="w-5 h-5 text-[#FF4D00] mx-auto mb-2" />
                <p className="text-sm text-[#F5F5F5] mb-1">No models loaded</p>
                <p className="text-xs text-[#A0A0A0] mb-3">Add your OpenRouter key in Settings.</p>
                <a href="/settings" className="text-xs text-[#FF4D00] hover:underline font-mono">Go to Settings</a>
              </div>
            ) : (
              <>
                <div className="p-2 border-b border-[#2A2A2A]">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      ref={searchRef}
                      type="text" value={search} onChange={e => setSearch(e.target.value)}
                      className="w-full pl-8 pr-8 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                      placeholder="Search models..."
                    />
                    {search && (
                      <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1 mt-2">
                    {['all', 'economy', 'standard', 'premium'].map(t => (
                      <button
                        key={t}
                        onClick={() => setTierFilter(t)}
                        className={`px-3 py-1.5 md:px-2 md:py-0.5 min-h-[44px] md:min-h-0 text-[9px] font-mono border transition-colors ${
                          tierFilter === t
                            ? t === 'all' ? 'bg-[#FF4D00] text-black border-[#FF4D00]'
                              : `border-current` + (t === 'economy' ? ' text-[#22C55E] bg-[#22C55E]/10' : t === 'standard' ? ' text-[#F59E0B] bg-[#F59E0B]/10' : ' text-[#EF4444] bg-[#EF4444]/10')
                            : 'border-[#2A2A2A] text-[#A0A0A0]'
                        }`}
                      >{t === 'all' ? 'ALL' : t.toUpperCase()}</button>
                    ))}
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {filtered.length === 0 ? (
                    <div className="p-4 text-center text-xs text-[#A0A0A0]">No models match "{search}"</div>
                  ) : (
                    filtered.slice(0, 50).map((m, mi) => {
                      const isSelected = currentModel === m.id;
                      return (
                        <button
                          key={m.id}
                          onClick={() => { onSelectModel(m.id, m.name, m.tier); setOpen(false); setSearch(''); }}
                          className={`w-full flex items-center justify-between px-3 py-2 min-h-[44px] md:min-h-0 text-left transition-colors border-b border-[#0F0F0F] ${
                            isSelected ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="text-[8px] w-12 text-center px-1 py-0.5 border font-mono shrink-0"
                              style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40' }}>
                              {m.tier?.toUpperCase()?.slice(0, 4)}
                            </span>
                            <div className="min-w-0 flex-1">
                              <span className="text-xs text-[#F5F5F5] block truncate">{m.name || m.id}</span>
                              <span className="text-[9px] text-[#A0A0A0] font-mono block truncate">{m.id}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            {m.context_length && <span className="text-[8px] font-mono text-[#A0A0A0]">{Math.round(m.context_length / 1000)}k</span>}
                            {isSelected && <Check className="w-3.5 h-3.5 text-[#FF4D00]" />}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
